import './ReportDocumentPageCover.scss';
import dayjs from 'dayjs';
import { QRCodeSVG } from 'qrcode.react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Project } from 'src/modules/common/types/Project';
import { UserSettings } from 'src/modules/common/types/UserSettings';
import { OutputDocumentPage } from 'src/modules/output/views/OutputDocumentPage';
import { OutputLogo } from 'src/modules/output/views/OutputLogo';
import { OutputPage } from 'src/modules/output/views/OutputPage';
import { ReportFormData } from 'src/modules/report/types/ReportFormData';
import { ReportGenerated } from 'src/modules/report/types/ReportGenerated';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly project: Project;
  readonly language: Language;
  readonly document: ReportGenerated;
  readonly formData: ReportFormData;
  readonly userSettings: UserSettings;
};

export const ReportDocumentPageCover = ({
  language,
  document,
  project,
}: Props): React.ReactElement => {
  const shareURl = `${window.location.origin}/open/${encodeURIComponent(project.id)}`;
  return (
    <OutputDocumentPage
      id="report-cover"
      order={1_000}
      section="cover"
      visible={true}
    >
      <OutputPage language={language} showPageNumber={false}>
        <div className="bp-report-document-page-cover">
          <div className="bp-report-document-page-cover__title">
            <FormattedMessage id="report/document/cover/title"/>
          </div>

          <div className="bp-report-document-page-cover__building">
            <div className="bp-report-document-page-cover__building__info">
              <div className="bp-report-document-page-cover__building--label">
                <FormattedMessage id="common/buildingType"/>
              </div>
              <div className="bp-report-document-page-cover__building--value">
                <FormattedMessage id={`common/buildingType/${document.cover.building.type}`}/>
              </div>
            </div>
            <div className="bp-report-document-page-cover__building__info">
              <div className="bp-report-document-page-cover__building--label">
                <FormattedMessage id="common/locaiton"/>
              </div>
              <div className="bp-report-document-page-cover__building--value">
                <FormattedMessage id={`common/country/name/${document.cover.building.address.country}`}/>
              </div>
            </div>
          </div>

          <div className="bp-report-document-page-cover__description">
            <div className="bp-report-document-page-cover__share-tip">
              <FormattedMessage id="report/document/scan-qrcode"/>
            </div>
            <div className="bp-report-document-page-cover__qrcode">
              <QRCodeSVG
                value={shareURl}
                level="L"
                size={256}
              />
            </div>
            <div className="bp-report-document-page-cover__link">
              {shareURl}
            </div>
          </div>

          <div className="bp-report-document-page-cover__description-details-section">
            <div className="bp-report-document-page-cover__description-details-title">
              <FormattedMessage id="report/document/cover/creationDate"/>
            </div>

            <div className="bp-report-document-page-cover__description-details-text">
              {dayjs(document.cover.reportDate).format('DD.MM.YYYY')}
            </div>
          </div>

          <div className="bp-report-document-page-cover__footer">
            <div className="bp-report-document-page-cover__footer-company">
              <div className="bp-report-document-page-cover__footer-title">
                {document.cover.organization}
              </div>

              <div className="bp-report-document-page-cover__footer-contacts">
                <div className="bp-report-document-page-cover__footer-contact">
                  {document.cover.address.street}
                  <br/>

                  {document.cover.address.country && `${document.cover.address.country} —`} {
                    document.cover.address.zip
                  } {document.cover.address.city}
                </div>

                <div className="bp-report-document-page-cover__footer-contact">
                  {document.cover.contact.phone}
                  <br/>
                  {document.cover.contact.email}
                </div>

                <div className="bp-report-document-page-cover__footer-contact">
                  {document.cover.contact.website}
                </div>
              </div>
            </div>

            <div className="bp-report-document-page-cover__footer-logo">
              <OutputLogo/>
            </div>
          </div>
        </div>
      </OutputPage>
    </OutputDocumentPage>
  );
};
